import React from 'react';
import { CandlestickChart, LineChart } from 'lucide-react';
import { CustomSelect, type CustomSelectOption } from '../CustomElements';
import { ThemeToggleGroup } from '../Theme';
import ChartToolbarButton from './ChartToolbarButton';
import {
  chartToolbarClasses,
  chartToolbarSelectClasses,
  getChartStyleToggleSegments,
  getChartToolbarSelectRootClassName,
} from './chartToolbarPresentation';

interface ChartToolbarProps {
  leading?: React.ReactNode;
  legend?: React.ReactNode;
  chartStyle?: React.ReactNode;
  actions?: React.ReactNode;
  settings?: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
}

export const ChartToolbar: React.FC<ChartToolbarProps> = ({
  leading,
  legend,
  chartStyle,
  actions,
  settings,
  trailing,
  className = '',
}) => (
  <div className={`${chartToolbarClasses.viewport} ${className}`}>
    <div className={chartToolbarClasses.row}>
      {leading && <div className={chartToolbarClasses.leading}>{leading}</div>}
      {legend && (
        <div aria-label="Chart legend" role="group" className={chartToolbarClasses.legend}>
          {legend}
        </div>
      )}
      {(chartStyle || settings || trailing) && (
        <div role="group" aria-label="Chart selectors" className={chartToolbarClasses.controls}>
          {chartStyle}
          {settings && <div className={chartToolbarClasses.settings}>{settings}</div>}
          {trailing && <div className={chartToolbarClasses.trailing}>{trailing}</div>}
        </div>
      )}
      {actions && (
        <div role="group" aria-label="Chart actions" className={chartToolbarClasses.actions}>
          {actions}
        </div>
      )}
    </div>
  </div>
);

interface ChartStyleToggleProps {
  value: 'candles' | 'line';
  onChange: (style: 'candles' | 'line') => void;
  showLine?: boolean;
}

export const ChartStyleToggle: React.FC<ChartStyleToggleProps> = ({ value, onChange, showLine = true }) => {
  const segments = getChartStyleToggleSegments(showLine);

  return (
    <ThemeToggleGroup aria-label="Chart style" className="shrink-0 gap-px">
      <ChartToolbarButton
        size="compact"
        iconOnly
        segment={segments.candles}
        label="Candlestick chart"
        icon={<CandlestickChart className="h-3.5 w-3.5" />}
        selected={value === 'candles'}
        onClick={() => onChange('candles')}
      />
      {showLine && (
        <ChartToolbarButton
          size="compact"
          iconOnly
          segment={segments.line}
          label="Line chart"
          icon={<LineChart className="h-3.5 w-3.5" />}
          selected={value === 'line'}
          onClick={() => onChange('line')}
        />
      )}
    </ThemeToggleGroup>
  );
};

interface ChartToolbarSelectProps {
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  title: string;
  widthClassName: string;
}

export const ChartToolbarSelect: React.FC<ChartToolbarSelectProps> = ({
  value,
  options,
  onChange,
  ariaLabel,
  title,
  widthClassName,
}) => (
  <CustomSelect
    value={value}
    options={options}
    onChange={onChange}
    ariaLabel={ariaLabel}
    triggerTitle={title}
    controlSize="xs"
    className={getChartToolbarSelectRootClassName(widthClassName)}
    buttonClassName={chartToolbarSelectClasses.button}
    menuClassName={chartToolbarSelectClasses.menu}
  />
);

interface ChartToolbarValueProps {
  children: React.ReactNode;
}

export const ChartToolbarValue: React.FC<ChartToolbarValueProps> = ({ children }) => (
  <span className="shrink-0 rounded-md border border-white/10 bg-white/5 px-1.5 py-1 text-[9px] font-black uppercase tracking-wider text-neon">
    {children}
  </span>
);
